/**
 * Help center — section nav scroll spy, related-question deep links, back to top.
 * Shared by Help center / Contact / Track order / Klarna / Payment Terms.
 */

/** @type {WeakMap<HTMLElement, AbortController>} */
const helpCenterControllers = new WeakMap();

/** px below header — higher value switches nav active to the next section sooner while scrolling */
const SCROLL_SPY_OFFSET = 260;

/** Max wait for bottom-drawer close before forcing unlock + scroll (iOS can stall onAnimationEnd). */
const DRAWER_CLOSE_TIMEOUT_MS = 400;

/** @param {HTMLElement} root */
function initHelpCenter(root) {
  helpCenterControllers.get(root)?.abort();

  const abortController = new AbortController();
  const { signal } = abortController;
  helpCenterControllers.set(root, abortController);

  root.addEventListener('click', (event) => onHelpCenterClick(event, root), { signal });
  initHelpCenterExclusiveAccordions(root, signal);
  initHelpCenterScrollSpy(root, signal);
  initHelpCenterHashDeepLink(root, signal);
  initHelpCenterMobileNav(root, signal);
  initHelpCenterMobileNavCollapse(root, signal);
  enhanceCalloutRollingLinks(root);
}

/** @param {HTMLElement} root @param {AbortSignal} signal */
function initHelpCenterHashDeepLink(root, signal) {
  const handleHash = () => {
    const hash = window.location.hash;
    if (!hash.startsWith('#faq-')) return;

    openFaqAndScroll(root, hash.slice(1));
  };

  requestAnimationFrame(handleHash);
  window.addEventListener('hashchange', handleHash, { signal });
}

/** @param {HTMLElement} root */
function destroyHelpCenter(root) {
  const navWrap = root.querySelector('[data-help-center-nav-wrap]');
  if (navWrap instanceof HTMLElement) {
    navWrap.inert = false;
  }

  helpCenterControllers.get(root)?.abort();
  helpCenterControllers.delete(root);
}

/**
 * Callout body is merchant RTE (`<p>Our <a>live chat</a>…</p>`). Rolling markup cannot be SSR’d inside RTE.
 * Copies already-translated link text into both lines; skips links that already have rolling.
 *
 * Standalone hosts (product cards, nav): `<div>` + line `<p class="wg-translate">` — Weglot often misses `<span>`.
 * Inside a sentence `<p>`: those tags split the DOM and isolate the link as a global string (footer “Live chat”).
 * Then use phrasing `<span>`s and skip `.wg-translate` so the parent `<p>` stays one Weglot unit.
 *
 * @param {HTMLElement} root
 */
function enhanceCalloutRollingLinks(root) {
  for (const link of root.querySelectorAll('[data-help-center-callout-body] a')) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    if (link.querySelector('[data-rolling-text-line]')) continue;

    const label = link.textContent?.trim() ?? '';
    if (label === '') continue;

    const insideParagraph = Boolean(link.closest('p'));
    const wrapTag = insideParagraph ? 'span' : 'div';
    const lineTag = insideParagraph ? 'span' : 'p';
    const translateClass = insideParagraph ? '' : ' wg-translate';

    const rolling = document.createElement(wrapTag);
    rolling.className = insideParagraph ? 'dev-rolling-text dev-rolling-text--inline' : 'dev-rolling-text';

    const viewport = document.createElement(wrapTag);
    viewport.className = 'dev-rolling-text__viewport';

    const track = document.createElement(wrapTag);
    track.className = 'dev-rolling-text__track';

    const line = document.createElement(lineTag);
    line.className = `dev-rolling-text__line${translateClass}`;
    line.setAttribute('data-rolling-text-line', '');
    line.textContent = label;

    const hoverLine = document.createElement(lineTag);
    hoverLine.className = `dev-rolling-text__line dev-rolling-text__line--hover${translateClass}`;
    hoverLine.setAttribute('data-rolling-text-line', '');
    hoverLine.setAttribute('aria-hidden', 'true');
    hoverLine.textContent = label;

    track.append(line, hoverLine);
    viewport.append(track);
    rolling.append(viewport);
    link.replaceChildren(rolling);
  }
}

/**
 * @param {number} ms
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
function delay(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const id = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id);
        resolve();
      },
      { once: true }
    );
  });
}

/** @param {Event} event @param {HTMLElement} root */
function onHelpCenterClick(event, root) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const relatedLink = target.closest('[data-help-center-related]');
  if (relatedLink instanceof HTMLAnchorElement) {
    event.preventDefault();
    const relatedId = relatedLink.dataset.helpCenterRelated;
    if (relatedId) {
      openFaqAndScroll(root, relatedId, { updateHash: true });
    }
    return;
  }

  const backTop = target.closest('[data-help-center-back-top]');
  if (backTop) {
    event.preventDefault();
    const topTarget = root.querySelector('#help-center-top');
    if (topTarget instanceof HTMLElement) {
      topTarget.focus({ preventScroll: true });
      scrollHelpCenterTargetIntoView(topTarget, root);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  const navLink = target.closest('[data-help-center-nav-link]');
  if (navLink instanceof HTMLAnchorElement) {
    event.preventDefault();
    const href = navLink.getAttribute('href');
    const sectionId = href?.startsWith('#') ? href.slice(1) : href;
    if (!sectionId) return;

    if (navLink.hasAttribute('data-help-center-nav-drawer-link')) {
      void navigateHelpCenterFromDrawer(root, sectionId);
      return;
    }

    scrollToHelpCenterSection(root, sectionId);
    history.replaceState(null, '', `#${sectionId}`);
  }
}

/** @returns {number} Document scroll Y, including while body is scroll-locked (`position: fixed`). */
function getHelpCenterDocumentScrollY() {
  if (document.body.style.position === 'fixed') {
    const lockedTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10));

    return Number.isFinite(lockedTop) ? lockedTop : 0;
  }

  return window.scrollY;
}

/**
 * Absolute document Y for a section heading, safe while the nav drawer holds body scroll-lock.
 * @param {HTMLElement} section
 * @param {HTMLElement} root
 */
function getHelpCenterSectionScrollTop(section, root) {
  const marker = section.querySelector('[data-help-center-section-heading]') ?? section;
  const padding = getHelpCenterScrollPaddingTop(root);

  return Math.max(0, marker.getBoundingClientRect().top + getHelpCenterDocumentScrollY() - padding);
}

/** @param {HTMLElement} root @param {string} sectionId */
async function navigateHelpCenterFromDrawer(root, sectionId) {
  const signal = helpCenterControllers.get(root)?.signal;
  const section = root.querySelector(`#${CSS.escape(sectionId)}`);
  if (!(section instanceof HTMLElement)) {
    await closeHelpCenterNavDrawer(root, signal);
    return;
  }

  // Measure before close — after unlock, iOS often drops the following smooth scroll.
  const targetTop = getHelpCenterSectionScrollTop(section, root);

  await closeHelpCenterNavDrawer(root, signal);
  if (signal?.aborted) return;

  history.replaceState(null, '', `#${sectionId}`);

  requestAnimationFrame(() => {
    if (signal?.aborted) return;

    requestAnimationFrame(() => {
      if (signal?.aborted) return;

      window.scrollTo({ top: targetTop, behavior: 'smooth' });

      // iOS Safari sometimes ignores smooth scroll right after clearing position:fixed.
      void delay(120, signal).then(() => {
        if (signal?.aborted) return;
        if (Math.abs(window.scrollY - targetTop) > 8) {
          window.scrollTo({ top: targetTop, behavior: 'instant' });
        }
      });
    });
  });
}

/** @param {HTMLElement} root @param {string} sectionId */
function scrollToHelpCenterSection(root, sectionId) {
  const section = root.querySelector(`#${CSS.escape(sectionId)}`);
  if (!(section instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    scrollHelpCenterTargetIntoView(section, root);
  });
}

/**
 * @param {HTMLElement} root
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
async function closeHelpCenterNavDrawer(root, signal) {
  const drawer = root.querySelector('[data-help-center-nav-drawer]');
  if (!(drawer instanceof HTMLElement) || !('closeDialog' in drawer)) return;

  const dialog = drawer.querySelector('dialog');
  const closePromise = /** @type {{ closeDialog: () => Promise<void> }} */ (drawer).closeDialog();

  // Bottom drawers animate with CSS transitions; DialogComponent waits onAnimationEnd (keyframes).
  // On iOS those keyframes can stall → body stays position:fixed and section scroll never runs.
  await Promise.race([closePromise, delay(DRAWER_CLOSE_TIMEOUT_MS, signal)]);
  if (signal?.aborted) return;

  if (dialog instanceof HTMLDialogElement && dialog.open) {
    dialog.close();
  }

  // Last resort if closeDialog stalled before #unlockBodyScroll ran.
  if (document.body.style.position === 'fixed') {
    const lockedTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10)) || 0;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: lockedTop, behavior: 'instant' });
  }
}

/** @param {HTMLElement} root @param {AbortSignal} signal */
function initHelpCenterMobileNav(root, signal) {
  const trigger = root.querySelector('[data-help-center-nav-trigger]');
  const drawer = root.querySelector('[data-help-center-nav-drawer]');
  if (!(trigger instanceof HTMLButtonElement) || !(drawer instanceof HTMLElement)) return;

  /** @returns {{ showDialog: () => void; closeDialog: () => Promise<void> } | null} */
  const getDrawer = () => {
    if ('showDialog' in drawer && 'closeDialog' in drawer) {
      return /** @type {{ showDialog: () => void; closeDialog: () => Promise<void> }} */ (drawer);
    }

    return null;
  };

  trigger.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      getDrawer()?.showDialog();
    },
    { signal }
  );

  drawer.addEventListener(
    'dialog:open',
    () => {
      trigger.setAttribute('aria-expanded', 'true');
    },
    { signal }
  );

  drawer.addEventListener(
    'dialog:close',
    () => {
      trigger.setAttribute('aria-expanded', 'false');
    },
    { signal }
  );
}

/**
 * Mobile: expanded section list on load (in-flow); sticky picker appears once that list scrolls away.
 * @param {HTMLElement} root
 * @param {AbortSignal} signal
 */
function initHelpCenterMobileNavCollapse(root, signal) {
  const navWrap = root.querySelector('[data-help-center-nav-wrap]');
  const mobileNav = root.querySelector('[data-help-center-mobile-nav]');
  if (!(navWrap instanceof HTMLElement) || !(mobileNav instanceof HTMLElement)) return;

  const mq = window.matchMedia('(max-width: 989px)');
  let frame = 0;

  const update = () => {
    if (!mq.matches) {
      root.classList.remove('is-mobile-nav-collapsed');
      navWrap.inert = false;
      return;
    }

    const headerOffset =
      Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')
      ) ||
      Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
      0;

    // Expanded list still in view under the sticky header → keep it; otherwise show sticky picker.
    const collapsed = navWrap.getBoundingClientRect().bottom <= headerOffset + 1;
    root.classList.toggle('is-mobile-nav-collapsed', collapsed);
    // Keep scrolled-away TOC out of tab order (otherwise Tab from sticky jumps back up).
    navWrap.inert = collapsed;
  };

  const scheduleUpdate = () => {
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = 0;
      update();
    });
  };

  document.addEventListener('scroll', scheduleUpdate, { signal, passive: true, capture: true });
  window.addEventListener('resize', scheduleUpdate, { signal, passive: true });
  mq.addEventListener('change', scheduleUpdate, { signal });
  scheduleUpdate();
}

/** @param {HTMLElement} root @param {HTMLDetailsElement} details */
function openExclusiveFaq(root, details) {
  for (const other of root.querySelectorAll('[data-help-center-faq-item]')) {
    if (other instanceof HTMLDetailsElement && other !== details) {
      other.open = false;
    }
  }

  details.open = true;
}

/** @param {HTMLElement} root */
function getHelpCenterScrollPaddingTop(root) {
  const probe = root.querySelector('[data-help-center-section]');

  if (probe instanceof HTMLElement) {
    const parsed = Number.parseFloat(getComputedStyle(probe).scrollMarginTop);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  const headerHeight = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height')
  );
  const stickyNavHeight = Number.parseFloat(
    getComputedStyle(root).getPropertyValue('--dev-help-center-mobile-nav-height')
  );
  const stickyNav =
    root.classList.contains('is-mobile-nav-collapsed') &&
    root.querySelector('[data-help-center-mobile-nav]')
      ? Number.isFinite(stickyNavHeight)
        ? stickyNavHeight
        : 44
      : 0;

  return (Number.isFinite(headerHeight) ? headerHeight : 0) + stickyNav + 24;
}

/** @param {Element} element @param {HTMLElement} root @param {ScrollBehavior} [behavior] */
function scrollHelpCenterTargetIntoView(element, root, behavior = 'smooth') {
  const top =
    element.getBoundingClientRect().top + window.scrollY - getHelpCenterScrollPaddingTop(root);
  window.scrollTo({ top: Math.max(0, top), behavior });
}

/** @param {HTMLElement} root @param {string} faqId @param {{ updateHash?: boolean }} [options] */
function openFaqAndScroll(root, faqId, { updateHash = false } = {}) {
  const details = root.querySelector(`#${CSS.escape(faqId)}`);
  if (!(details instanceof HTMLDetailsElement)) return;

  openExclusiveFaq(root, details);

  if (updateHash) {
    history.replaceState(null, '', `#${faqId}`);
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollHelpCenterTargetIntoView(details, root);

      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.focus({ preventScroll: true });
      }
    });
  });
}

/** @param {HTMLElement} root @param {AbortSignal} signal */
function initHelpCenterExclusiveAccordions(root, signal) {
  const detailsElements = root.querySelectorAll('[data-help-center-faq-item]');

  for (const element of detailsElements) {
    if (!(element instanceof HTMLDetailsElement)) continue;

    element.addEventListener(
      'toggle',
      () => {
        if (!element.open) return;

        for (const other of detailsElements) {
          if (other instanceof HTMLDetailsElement && other !== element) {
            other.open = false;
          }
        }
      },
      { signal }
    );
  }
}

/** @param {HTMLElement} root @param {AbortSignal} signal */
function initHelpCenterScrollSpy(root, signal) {
  const navItems = Array.from(root.querySelectorAll('[data-help-center-nav-item]')).filter(
    (item) => item instanceof HTMLElement
  );
  const sections = Array.from(root.querySelectorAll('[data-help-center-section][id]')).filter(
    (section) => section instanceof HTMLElement
  );

  if (!navItems.length || !sections.length) return;

  const firstSection = sections[0];
  if (!firstSection) return;

  /** @param {string} activeId */
  const setActiveNavItem = (activeId) => {
    for (const item of navItems) {
      const href = item.querySelector('[data-help-center-nav-link]')?.getAttribute('href');
      const sectionId = href?.startsWith('#') ? href.slice(1) : href;
      item.classList.toggle('is-active', sectionId === activeId);
    }

    const labelEl = root.querySelector('[data-help-center-nav-label]');
    const activeLink = root.querySelector(
      `[data-help-center-nav-link][href="#${CSS.escape(activeId)}"]`
    );
    if (labelEl instanceof HTMLElement && activeLink) {
      const rollingLine = activeLink.querySelector('[data-rolling-text-line]');
      const labelSource = rollingLine ?? activeLink;
      labelEl.textContent = labelSource.textContent?.trim() ?? '';
    }
  };

  const getActivationLine = () => {
    const headerHeight = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );

    return (Number.isFinite(headerHeight) ? headerHeight : 0) + SCROLL_SPY_OFFSET;
  };

  let frame = 0;

  const updateActiveNavItem = () => {
    const activationLine = getActivationLine();
    let activeId = firstSection.id;

    for (const section of sections) {
      const marker = section.querySelector('[data-help-center-section-heading]') ?? section;
      if (marker.getBoundingClientRect().top <= activationLine) {
        activeId = section.id;
      }
    }

    setActiveNavItem(activeId);
  };

  const scheduleUpdate = () => {
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = 0;
      updateActiveNavItem();
    });
  };

  document.addEventListener('scroll', scheduleUpdate, { signal, passive: true, capture: true });
  window.addEventListener('resize', scheduleUpdate, { signal, passive: true });
  window.addEventListener('hashchange', scheduleUpdate, { signal });
  scheduleUpdate();
}

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof HTMLElement) {
    event.target.querySelectorAll('dev-help-center').forEach((element) => {
      if (element instanceof HTMLElement) {
        destroyHelpCenter(element);
      }
    });
  }
});

class DevHelpCenter extends HTMLElement {
  connectedCallback() {
    initHelpCenter(this);
  }

  disconnectedCallback() {
    destroyHelpCenter(this);
  }
}

if (!customElements.get('dev-help-center')) {
  customElements.define('dev-help-center', DevHelpCenter);
}
